import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

// UI-Components
import { Button } from 'ui-components/components/button';
import { LoadingIndicator } from 'ui-components/components/loading-indicator';
import { Modal } from 'ui-components/components/modal';
import { ModalBody } from 'ui-components/components/modal/modal-body';
import { ModalFooter } from 'ui-components/components/modal/modal-footer';
import { ModalHeader } from 'ui-components/components/modal/modal-header';
import { ModalFrameworkMethodsPropType } from 'ui-components/prop-types/modal-framework-methods';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import { messages } from './messages';
import style from './void-label-modal.module.less';
import LabelCard from '../../../components/label-card';
import { emptyArray } from 'ui-components/default-props/empty';

// Services
import { FulfillmentPlanOrderGridStatus } from '../../../enums/fulfillmentplan-order-grid-status';
import { isLocalDeliveryServiceSelectedForFulfillmentPlan } from '../../../hooks/use-is-local-delivery-service-selected-for-fulfillment-plan';
import { bulkVoidRetry } from '../../../store/bulk/void/bulk.void.actions';
import Global from '../../../store/global-selectors';
import { goForwardOnJob } from '../../../store/job-pipeline/job-pipeline.actions';

// Components/Containers

export const VoidLabelModal = ({ modalFrameworkMethods, skipWarning = false, jobId = null, acceptWarningAndVoid, retryVoid }) => {
  const isLoading = useSelector(Global.selectLabelsLoading);
  const [skippedOrAcceptedWarning, setSkippedOrAcceptedWarning] = useState(skipWarning);
  const fulfillments = useSelector(Global.selectLabelsFulfillments) || emptyArray;
  const fulfillmentIdsFromFulfillments = fulfillments.map(({ fulfillmentId }) => fulfillmentId);
  const { fulfillmentIds = fulfillmentIdsFromFulfillments } = useSelector((state) =>
    Global.selectJobPipelineJobDataByJobId(state, { jobId }),
  );
  const numberOfFulfillments = fulfillments.length || fulfillmentIds.length;
  const successfullyVoided = useSelector(Global.selectLabelsSuccessfullyVoided);
  const voidWarnings = useSelector(Global.selectLabelsVoidWarnings);
  const fulfillmentPlanIds = useSelector((state) => Global.selectEntitiesFPIdsByFulfillmentIds(state, { fulfillmentIds })) || emptyArray;
  const localDeliveryFPs = useSelector((state) =>
    fulfillmentPlanIds.filter((fpId) => isLocalDeliveryServiceSelectedForFulfillmentPlan(state, fpId)),
  );

  const dispatch = useDispatch();
  const onAcceptWarningAndVoid = useCallback(() => {
    setSkippedOrAcceptedWarning(true);
    if (acceptWarningAndVoid) {
      return acceptWarningAndVoid();
    }
    dispatch(goForwardOnJob(jobId));
  }, [acceptWarningAndVoid, dispatch, jobId]);

  const onRetryVoid = useCallback(() => {
    if (retryVoid) {
      return retryVoid();
    }
    dispatch(bulkVoidRetry(jobId));
  }, [dispatch, jobId, retryVoid]);

  const renderLabelListSection = useCallback((labelCards, formattedMessageProps) => {
    if (labelCards.length > 0) {
      return (
        <div className={style['label-list-section']}>
          <div className={style['label-list-title']}>
            <FormattedMessage {...formattedMessageProps} />
          </div>
          <div className={style['label-list-section-list']}>
            {labelCards.map((labelCardProps, index) => (
              <LabelCard
                key={labelCardProps?.fulfillmentId ?? index}
                carrierId={labelCardProps?.fulfillment?.carrierId}
                className={style['label-card']}
                orderGridStatus={FulfillmentPlanOrderGridStatus.Shipped.name}
                serviceId={labelCardProps?.fulfillment?.serviceId}
                trackingInformation={labelCardProps?.fulfillment?.trackingInformation}
                {...labelCardProps}
              />
            ))}
          </div>
        </div>
      );
    }
  }, []);

  const getModalTitleDescriptor = () => {
    if (numberOfFulfillments && localDeliveryFPs.length === numberOfFulfillments) {
      return messages.cancelBooking;
    }
    if (localDeliveryFPs.length === 0) {
      return messages.voidLabel;
    }
    return messages.voidLabelAndCancelBooking;
  };
  const modalTitleDescriptor = getModalTitleDescriptor();
  const bookingCount = localDeliveryFPs.length;
  const labelCount = numberOfFulfillments - bookingCount;
  const carrierName = 'Uber'; // TODO: Update messaging to not include carrier name when multiple local delivery carriers exist.

  const renderWarningMessageTitle = useCallback(() => {
    return (
      <div>
        {labelCount > 0 && bookingCount > 0 && (
          <FormattedMessage
            {...messages.requestToContinueLabelsAndBookings}
            values={{ numberOfFulfillments, b: (chunks) => <b>{chunks}</b> }}
          />
        )}
        {bookingCount === numberOfFulfillments && (
          <FormattedMessage {...messages.requestToContinueBookings} values={{ bookingCount, b: (chunks) => <b>{chunks}</b> }} />
        )}
        {labelCount === numberOfFulfillments && (
          <FormattedMessage {...messages.requestToContinueLabels} values={{ numberOfFulfillments, b: (chunks) => <b>{chunks}</b> }} />
        )}
      </div>
    );
  }, [bookingCount, labelCount, numberOfFulfillments]);

  // To make sure that the event is not passed as the first argument to closeModal framework method which is actually expecting isCancel as first arg
  const { closeModal, cancelModal } = modalFrameworkMethods;
  const _closeModal = useCallback(() => closeModal(), [closeModal]);

  const skippedOrAcceptedWarningContent = isLoading ? (
    <div className={style['loading-indicator-wrapper']}>
      <LoadingIndicator className={style['loading-indicator']} />
    </div>
  ) : (
    <div className={style['label-list']}>
      {renderLabelListSection(successfullyVoided, messages.successfullyVoided)}
      {renderLabelListSection(voidWarnings, messages.voidWarnings)}
    </div>
  );

  return (
    <Modal className={style['void-label-modal']}>
      <ModalHeader closeModal={successfullyVoided.length ? _closeModal : cancelModal}>
        <FormattedMessage {...modalTitleDescriptor} values={{ labelCount, bookingCount }} />
      </ModalHeader>
      <ModalBody className={style['modal-body']}>
        {!skippedOrAcceptedWarning && (
          <div className={style['warning-message']}>
            {renderWarningMessageTitle()}
            <div>
              {labelCount > 0 && (
                <div>
                  <FormattedMessage {...messages.warningMessageLabels} />
                </div>
              )}
              {bookingCount > 0 && (
                <>
                  <div>
                    <FormattedMessage {...messages.warningMessageBookings} values={{ carrierName }} />
                  </div>
                  <ul className={style['booking-warning-list']}>
                    <FormattedMessage
                      {...messages.warningMessageBookingsList}
                      values={{
                        li: (chunks) => <li className={style['booking-warning']}>{chunks}</li>,
                      }}
                    />
                  </ul>
                </>
              )}
            </div>
          </div>
        )}
        {skippedOrAcceptedWarning ? skippedOrAcceptedWarningContent : null}
      </ModalBody>
      {!isLoading && (
        <ModalFooter className={style['modal-footer']}>
          {!skippedOrAcceptedWarning && (
            <>
              <Button className={style['cancel-button']} onClick={cancelModal} theme={THEME_CONSTANTS.tertiary}>
                <FormattedMessage {...messages.cancel} />
              </Button>
              <Button className={style['continue-button']} onClick={onAcceptWarningAndVoid} theme={THEME_CONSTANTS.primary}>
                <FormattedMessage {...messages.continue} />
              </Button>
            </>
          )}
          {skippedOrAcceptedWarning && voidWarnings.length > 0 ? (
            <Button className={style['retry-void-button']} onClick={onRetryVoid} theme={THEME_CONSTANTS.tertiary}>
              <FormattedMessage {...messages.retryVoid} />
            </Button>
          ) : null}
          {skippedOrAcceptedWarning ? (
            <Button className={style['done-button']} onClick={_closeModal} theme={THEME_CONSTANTS.primary}>
              <FormattedMessage {...messages.done} />
            </Button>
          ) : null}
        </ModalFooter>
      )}
    </Modal>
  );
};

VoidLabelModal.propTypes = {
  modalFrameworkMethods: ModalFrameworkMethodsPropType.isRequired,
  skipWarning: PropTypes.bool,
  jobId: PropTypes.string,
  acceptWarningAndVoid: PropTypes.func,
  retryVoid: PropTypes.func,
};
